import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jobDescriptionAPI } from '../services/api';

// Deschide PDF-ul fișei postului într-un tab nou (preview inline). Folosește fetch + blob
// ca să atașeze Authorization (endpoint-ul cere auth).
const openJobDescriptionPdf = async (instanceId) => {
  try {
    const token = localStorage.getItem('authToken');
    const res = await fetch(jobDescriptionAPI.getPdfUrl(instanceId), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (_) {
    /* silently fail */
  }
};

// JobDescriptionFlowCards — afișează status fișa postului SSM/SU pentru angajatul logat.
// Materializează lazy via GET /my-flow. Apare în MyCourses, integrat ca pas final după
// onboarding-ul SSM/SU (cu prop `filterDomain` poate fi randată doar pentru un domain).
// Returnează `null` dacă angajatul nu are nicio fișă materializată în domain-ul filtrat.
//
// Props:
//   filterDomain — 'SSM' | 'SU' | undefined (ambele). Folosit pentru a o randa imediat
//                  după grupul corespunzător de cursuri (continuitate vizuală cu săgețile).
const JobDescriptionFlowCards = ({ filterDomain, standalone = false }) => {
  const { t } = useTranslation();
  const [flow, setFlow] = useState({ ssm: null, su: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await jobDescriptionAPI.getMyFlow();
        if (!mounted) return;
        setFlow(data || { ssm: null, su: null });
      } catch (_) {
        // Silent — feature opțional
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;

  // Afișăm domeniile care au nevoie de atenție: instance reală sau status TEMPLATE_MISSING.
  // Ascundem WAITING_PREREQUISITE (angajatul nu a terminat încă instruirile prereq).
  const domains = filterDomain ? [filterDomain.toLowerCase()] : ['ssm', 'su'];
  const renderable = domains
    .map((d) => flow[d])
    .filter(
      (item) =>
        item &&
        ['WAITING_EMPLOYEE', 'WAITING_COUNTERPART', 'FULLY_SIGNED', 'TEMPLATE_MISSING'].includes(
          item.status
        )
    );

  if (renderable.length === 0) return null;

  const renderCard = (item) => {
    const domain = item.domain;
    const isSSM = domain === 'SSM';
    const titleColor = isSSM
      ? 'text-primary-700 dark:text-primary-300'
      : 'text-danger-700 dark:text-warning-300';
    const borderColor = isSSM ? 'border-l-primary-500' : 'border-l-danger-500';
    const subtitleColor = isSSM
      ? 'bg-primary-50 dark:bg-primary-950/40'
      : 'bg-danger-50 dark:bg-warning-950/40';

    let statusLabel,
      statusColor,
      primaryAction,
      hideViewAction = false;
    if (item.status === 'WAITING_EMPLOYEE') {
      statusLabel = t('jobDescription.statusWaitingEmployee', 'În așteptarea semnăturii tale');
      statusColor = 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      primaryAction = (
        <Link to={`/job-descriptions/${item.instanceId}/sign`}>
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition">
            {t('jobDescription.signNow', 'Semnează acum')}
          </button>
        </Link>
      );
    } else if (item.status === 'WAITING_COUNTERPART') {
      // Fișa postului se semnează doar de angajat + administrator firmă (SSM/PSI nu intervin).
      statusLabel = t('jobDescription.statusWaitingAdmin', 'Așteaptă semnătura administratorului');
      statusColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      primaryAction = null;
    } else if (item.status === 'TEMPLATE_MISSING') {
      statusLabel = t('jobDescription.statusTemplateMissing', 'Firma SSM nu a definit template-ul');
      statusColor = 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200';
      primaryAction = null;
      hideViewAction = true; // fără PDF de văzut
    } else {
      statusLabel = t('jobDescription.statusFullySigned', 'Semnată complet');
      statusColor = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      primaryAction = null;
    }

    // Butonul „ochi" (view PDF) — disponibil mereu, alături de primary action.
    const viewAction = (
      <button
        onClick={() => openJobDescriptionPdf(item.instanceId)}
        title={t('jobDescription.viewPdf', 'Vezi PDF-ul fișei postului')}
        aria-label={t('jobDescription.viewPdf', 'Vezi PDF-ul fișei postului')}
        className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      </button>
    );

    return (
      <div
        key={domain}
        className={`bg-white dark:bg-slate-900 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-800 border-l-4 ${borderColor} p-4`}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className={`font-bold ${titleColor}`}>
              {t('jobDescription.cardTitle', { domain, defaultValue: `Fișa postului — ${domain}` })}
            </h3>
            <p
              className={`text-xs px-2 py-0.5 rounded-full inline-block mt-1 ${subtitleColor} ${titleColor}`}
            >
              {isSSM ? 'SSM' : 'SU'} · {t('jobDescription.bilingualBadge', 'Bilingv RO/EN')}
            </p>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor} whitespace-nowrap`}
          >
            {statusLabel}
          </span>
        </div>
        <div className="flex justify-end items-center gap-2">
          {!hideViewAction && viewAction}
          {primaryAction}
        </div>
      </div>
    );
  };

  // Când e filtrată pe un singur domain, randăm cardul ca o continuare a grupului de cursuri:
  // săgeată „↓" în jos + un singur card (fără header de secțiune separat). În modul `standalone`
  // (fără grup de onboarding deasupra — ex: cursant doar-IP) scoatem săgeata și marginea negativă.
  if (filterDomain) {
    return (
      <div className={`${standalone ? 'mb-4' : '-mt-2 mb-4'} flex flex-col items-stretch`}>
        {!standalone && (
          <div className="flex justify-center mb-1">
            <svg
              className="w-5 h-5 text-slate-400 dark:text-slate-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        )}
        <div className="grid grid-cols-1 gap-3">{renderable.map(renderCard)}</div>
      </div>
    );
  }

  // Fallback (fără filtru) — block separat cu titlu, ca înainte
  return (
    <div className="mt-4">
      <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-3">
        {t('jobDescription.flowSectionTitle', 'Fișa postului')}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{renderable.map(renderCard)}</div>
    </div>
  );
};

export default JobDescriptionFlowCards;
